/**
 * Reference slides — the lift of the text and one progress bar for the whole
 * stack.
 *
 * The lift (85_blocks.sass): a slide is one `--reference-lift` taller than the
 * viewport with its photo sticky, so the text scrolls on once the photo has
 * stopped. Per slide it is the distance from the top of the text group
 * (`[data-reference-text]`) up to its scroll-margin-top, the line the section
 * headlines sit on — the text stops there whatever the title's line count and
 * never runs under the header. Only slides that roll over (sticky) get one.
 * Measured when the text or the viewport changes size, never while scrolling:
 * the motion itself is plain sticky layout.
 *
 * The bar (`[data-reference-progress]`, rendered inside the last slide,
 * position: fixed) is shown while a slide sits under the viewport middle and
 * fills continuously with the scroll: 1/n once the first slide is fully in
 * view, 1 when the last one is.
 */
import { flowTop } from './scroll'

const bar = document.querySelector('[data-reference-progress]')
const slides = [...document.querySelectorAll('[data-reference-slide]')]

const lift = () => {
  for (const slide of slides) {
    const text = slide.querySelector('[data-reference-text]')
    if (!text || getComputedStyle(slide).position !== 'sticky') {
      slide.style.removeProperty('--reference-lift')
      continue
    }
    // offsetTop: the slide is the offset parent, so its sticky offset is not in it
    const stop = parseFloat(getComputedStyle(text).scrollMarginTop) || 0
    slide.style.setProperty(
      '--reference-lift',
      `${Math.max(0, Math.round(text.offsetTop - stop))}px`
    )
  }
}

if (slides.length) {
  const observer = new ResizeObserver(lift)
  slides.forEach((slide) => {
    const text = slide.querySelector('[data-reference-text]')
    if (text) observer.observe(text, { box: 'border-box' })
  })
  window.addEventListener('resize', lift)
  // The dev server injects the CSS after the modules have run (like stack.js)
  window.addEventListener('load', lift)
  lift()
}

if (bar && slides.length) {
  const count = slides.length

  let ticking = false
  const update = () => {
    ticking = false
    const viewport = window.innerHeight
    const first = flowTop(slides[0])
    const last = flowTop(slides[count - 1])
    const y = window.scrollY

    const visible = y >= first - viewport / 2 && y <= last + viewport / 2
    const travel = Math.max(1, last - first)
    const t = Math.min(1, Math.max(0, (y - first) / travel))
    const fill = count > 1 ? (1 + t * (count - 1)) / count : 1

    bar.style.setProperty('--progress', fill.toFixed(4))
    bar.classList.toggle('is-visible', visible)
  }

  const request = () => {
    if (!ticking) {
      ticking = true
      window.requestAnimationFrame(update)
    }
  }

  window.addEventListener('scroll', request, { passive: true })
  window.addEventListener('resize', request)
  window.addEventListener('load', request)
  request()
}
